/**
 * browserExecutionReasonVocabulary — the one shared vocabulary for a browser-work SESSION's
 * `execution.blocked_reason`: why an already-approved bounded browser-work session stopped.
 *
 * This is a DIFFERENT field from the browser-worker capability's `blocked_reason`, which
 * `browserCapabilityVocabulary` owns. Same name, same Hebrew surfaces, different producers and a
 * different scale — and the session field's scale is a superset, which is why the two cannot share
 * one map in either direction:
 *
 *   - the capability is the DEPLOYMENT's readiness verdict (`routers.connections
 *     .browser_worker_capability()`), five tokens, and it is written once at request time;
 *   - the session field is written by `app.services.browser_work_sessions._session_record` from
 *     TWO sources (`:311-312` the request-time capability token, `:327-328` the session's own
 *     stored `ExecutionStateRecord.blocked_reason`), so it can hold a capability token **or** any
 *     reason the bounded executor refuses with. On the pilot default the first arm is what an
 *     operator sees — the request is recorded while live execution is unavailable
 *     (`hosted_browser_backend_disabled`) and no run is ever created.
 *
 * The reachable set is therefore the UNION of three producer modules, derived from their writers
 * rather than from a render sweep:
 *
 *   1. `browser_work_executor.REASON_*` that reach the field. The module declares seventeen
 *      `REASON_*` constants, and only TEN can reach `blocked_reason`: the field is written by
 *      `_block_and_commit(reason=…)`, `_record_execution_state(blocked_reason=…)` and
 *      `_failure_record(reason=…)`. The other seven (`no_bounded_action_recorded`,
 *      `credential_already_deposited_for_session`, `session_is_not_waiting_for_credential`,
 *      `session_has_not_been_approved_yet`, `session_approval_already_recorded`,
 *      `session_has_finished`, `login_required_is_only_supported_for_write_actions`) travel ONLY
 *      through `_refuse` / `_terminal_detail` (`:1094-1127`), which build an `HTTPException` whose
 *      detail is already a Hebrew sentence — they reach an API caller, never this stored field, so
 *      a map key for them would be a key this producer cannot mint.
 *   2. Three string literals written straight into the field, with no constant at all:
 *      `approval_verification_failed` (`:1245`), `session_approval_no_longer_valid` (`:1487`) and
 *      `execution_transport_error` (`:1918`). A sweep for `REASON_` constants alone goes short by
 *      exactly these three, which is why this module's parity test reads the executor's source for
 *      the literals as well as the constants.
 *   3. `openai_hosted_browser_adapter`'s own refusal vocabulary, because the executor FORWARDS it:
 *      `_execute_read_only_arm` catches `HostedBrowserRefusal` and stores `refusal.reason` verbatim
 *      (`:2142`), and `OpenAIHostedBrowserAdapter.execute` stores `refusal.reason` into the run
 *      (`:1161`), which `:2170` reads back. That is the adapter's eight `BLOCKED_*` and six
 *      `REFUSAL_*` constants. Five of the eight `BLOCKED_*` are also the capability's five — the
 *      SAME tokens, spread in below from the capability map so there is one wording, not two.
 *
 * `approval_not_recorded` and `request_not_bound` are named although the executor's call sites
 * always pass a named approver and a bound request: the field is a verbatim passthrough of
 * `refusal.reason`, so the adapter's refusal vocabulary — not a precondition analysis — is what
 * this producer can put here. A named key that never arrives renders nothing; an unnamed one that
 * does arrive would print an English token inside a Hebrew line, which is the defect this module
 * exists to remove.
 *
 * Why an unrecognised value is labelled rather than echoed. Every value either producer can write
 * is an identifier from a constant block or a literal in the source — the operator-facing prose
 * goes into the record's `detail` and `steps`, never here — so a value this map does not name
 * carries nothing an operator can read, and echoing it would put another English token in the
 * Hebrew line this module exists to clean. That is the treatment `browserCapabilityVocabulary` and
 * `esetProtectionVocabulary.protectionStatusMeta` give an arm they do not name, and it is the
 * opposite of `errorReasonVocabulary`'s choice for `technical_trace.error`, which is a token set
 * WITH prose writers. The cost is stated rather than hidden: a producer rename reads as the
 * unrecognised wording instead of as the new token, so the parity pin in this module's test — which
 * reads all three producer modules — is the only control that sees the drift.
 *
 * The wire is unchanged by all of this: no token is added, removed or reinterpreted, and all three
 * producers keep writing exactly what they wrote before. This is a labelling change on read-only
 * surfaces.
 */

import { BROWSER_CAPABILITY_BLOCKED_HE } from "./browserCapabilityVocabulary";

export const BROWSER_EXECUTION_REASON_HE = {
  // The five the request-time capability can also deposit here (same tokens, one wording).
  ...BROWSER_CAPABILITY_BLOCKED_HE,

  // ── the bounded executor's own reasons (app.services.browser_work_executor) ──────────────────
  api_capability_available_browser_not_entered:
    "קיימת יכולת API מאושרת לפעולה זו — הביצוע הופנה לנתיב ה-API ולא נפתח סשן דפדפן",
  dev_lab_simulated_mode_required:
    "נתיב ביצוע עבודת הדפדפן פועל במצב מעבדה מדומה בלבד — המצב המוגדר אינו מאפשר ביצוע",
  live_browser_write_arm_unavailable:
    "אין מסלול כתיבה חי — פעולה משנה מצב חסומה בכל תצורה",
  credential_handoff_window_expired:
    "חלון מסירת אישורי ההתחברות פג — ההמתנה לסשן בוטלה",
  no_live_session_credential:
    "לא היו אישורי התחברות חיים לסשן בזמן הביצוע",
  postcondition_read_back_mismatch:
    "הקריאה החוזרת לא תאמה את הפעולה המאושרת לאחר ביצועה — ייתכן שהשינוי בוצע ואינו מאומת",
  hosted_read_only_path_refused:
    "מסלול הקריאה בלבד של ספק הדפדפן סירב לבקשה",
  lab_login_refused:
    "התחברות במסלול המעבדה נדחתה",
  lab_record_not_found:
    "הרשומה המבוקשת לא נמצאה במסלול המעבדה",
  lab_record_already_exists:
    "רשומה זהה כבר קיימת במסלול המעבדה",

  // ── literals written into the field with no constant (browser_work_executor) ─────────────────
  session_approval_no_longer_valid:
    "אסימון האישור של הסשן אינו בתוקף עוד",
  execution_transport_error:
    "שגיאת תעבורה בביצוע התחום — הסשן נכשל",

  approval_verification_failed:
    "אימות אסימון האישור נכשל — הביצוע נחסם לפני כל מגע עם היעד",
  // ── the hosted browser adapter's own refusals, forwarded verbatim by the executor ────────────
  origin_not_allow_listed:
    "היעד המבוקש אינו ברשימת היעדים המאושרים",
  hosted_browser_transport_error:
    "שגיאת תעבורה מול ספק הדפדפן — דבר לא בוצע מול היעד",
  objective_is_not_read_only:
    "המטרה אינה מנוסחת כקריאה בלבד — הבקשה נדחתה לפני כל ביצוע",
  objective_invalid:
    "המטרה שנשלחה אינה תקינה — ריקה, ארוכה מהמותר או שאינה טקסט",
  objective_contains_credential:
    "המטרה נראית כמכילה סיסמה, מפתח או אסימון — הבקשה נדחתה ולא נשמרה",
  approval_not_recorded:
    "לא נרשם מאשר אנושי לבקשה — הביצוע נדחה",
  request_not_bound:
    "הבקשה אינה מקושרת לעובד, לכרטיס ולמזהה מדויקים — הביצוע נדחה",
  dev_lab_path_requires_simulated_mode:
    "מסלול המעבדה דורש מצב מדומה מוגדר — המצב הנוכחי אינו מאפשר ביצוע",
  live_path_requires_live_mode:
    "מסלול הביצוע החי דורש מצב חי מוגדר — המצב הנוכחי אינו מאפשר ביצוע",
};

/** A session `blocked_reason` this build does not name. Named rather than echoed — see the header. */
export const BROWSER_EXECUTION_REASON_UNKNOWN = "סיבת החסימה/הכשלון לא זוהתה בגרסה זו";

/**
 * The Hebrew wording for a browser-work session `execution.blocked_reason`. A missing value and a
 * value this build does not name both read as the unrecognised arm — never as the raw token.
 */
export function browserExecutionReasonLabel(value) {
  return BROWSER_EXECUTION_REASON_HE[value] || BROWSER_EXECUTION_REASON_UNKNOWN;
}
